/**
 * 🎯 ADMIN VERIFICATION DASHBOARD
 * Integration page that combines new verification endpoints with existing admin dashboard
 * Can be added as a new tab/route to the existing admin system
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import {
  VerifiedUser,
  Schedule,
  CheckCircle,
  Cancel,
  TrendingUp,
  Assessment,
  ListAlt,
} from '@mui/icons-material';
import AdminVerificationRequestsList from '@/components/verification/AdminVerificationRequestsList';
import { VerificationRequirementsPage } from '../admin/VerificationRequirementsPage';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`verification-tabpanel-${index}`}
      aria-labelledby={`verification-tab-${index}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

interface DashboardStats {
  total: number;
  pending: number;
  verified: number;
  rejected: number;
}

const AdminVerificationDashboard: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState<DashboardStats>({
    total: 0,
    pending: 0,
    verified: 0,
    rejected: 0
  });

  const handleStatsUpdate = (newStats: DashboardStats) => {
    setStats(newStats);
  };

  const getSuccessRate = () => {
    if (stats.total === 0) return 0;
    return Math.round((stats.verified / (stats.verified + stats.rejected)) * 100) || 0;
  };

  const getPendingPercentage = () => {
    if (stats.total === 0) return 0;
    return Math.round((stats.pending / stats.total) * 100);
  };

  return (
    <Box p={3}>
      {/* Page Header */}
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Verification Management
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage user verifications and unverified booking rules
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="fullWidth"
        >
          <Tab icon={<ListAlt />} label="Verification Requests" />
          <Tab icon={<VerifiedUser />} label="Verification Requirements" />
        </Tabs>
      </Paper>

      {/* Tab 0: Verification Requests */}
      <TabPanel value={tabValue} index={0}>
        {/* Statistics Cards */}
        <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Total Requests
                  </Typography>
                  <Typography variant="h4">
                    {stats.total}
                  </Typography>
                </Box>
                <Assessment color="primary" sx={{ fontSize: 40, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Pending Review
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.pending}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {getPendingPercentage()}% of total
                  </Typography>
                </Box>
                <Schedule color="warning" sx={{ fontSize: 40, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Verified
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {stats.verified}
                  </Typography>
                  <Chip 
                    label={`${getSuccessRate()}% success rate`}
                    color="success"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
                <CheckCircle color="success" sx={{ fontSize: 40, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Rejected
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {stats.rejected}
                  </Typography>
                </Box>
                <Cancel color="error" sx={{ fontSize: 40, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

        {/* Verification Requests List */}
        <Paper elevation={2}>
          <AdminVerificationRequestsList onStatsUpdate={handleStatsUpdate} />
        </Paper>
      </TabPanel>

      {/* Tab 1: Verification Requirements */}
      <TabPanel value={tabValue} index={1}>
        <VerificationRequirementsPage />
      </TabPanel>
    </Box>
  );
};

export default AdminVerificationDashboard;